import { CallEmsApi, iEmsApiResponseBody, networkFailure } from "../Logic/TenantAPI";
import { toEmsApiTimestamp } from "../Logic/emsDates";

/*
 * DTOs for the two Core mail-processing endpoints.
 *
 * Every aggregate is OPTIONAL, not nullable. The API sets
 * DefaultIgnoreCondition = WhenWritingNull, so a null property is absent from
 * the JSON entirely — `count?: number` is the accurate type and
 * `count: number | null` describes a value that never arrives. The OpenAPI
 * document says "nullable": true for these, which is misleading.
 *
 * An absent aggregate means the collection could not measure it. It is not
 * zero, and must never be coerced to zero. See contract §2.1.
 */

export type TenantCollectionHealth = "Healthy" | "Failing" | "Stale" | "NeverCollected" | "Disabled";

export type TenantCollectionOutcome = "Success" | "Failed" | "LockTimeout";

export interface TenantInFlightStatus {
    count?: number;
    oldestAt?: string;
    /** Server-computed against a server-side threshold. Never re-derive this. */
    stalled: boolean;
    /** .NET TimeSpan, e.g. "00:52:10". Parse with parseEmsTimeSpan. */
    oldestAge?: string;
}

export interface TenantInFlightCounts {
    inboundQueued?: TenantInFlightStatus;
    inboundProcessing?: TenantInFlightStatus;
    outboundQueued?: TenantInFlightStatus;
    outboundBuilding?: TenantInFlightStatus;
    outboundSendable?: TenantInFlightStatus;
}

export interface TenantTerminalCounts {
    inboundFailed?: number;
    inboundDeadLettered?: number;
    outboundFailed?: number;
    outboundDeadLettered?: number;
    outboundAbandoned?: number;
}

/** Deltas over the snapshot interval. These are the only fields that may be summed across time. */
export interface TenantThroughputDeltas {
    received?: number;
    sent?: number;
}

export interface TenantMailboxRollup {
    count?: number;
    unhealthyCount?: number;
    receiveFailedCount?: number;
    sendFailedCount?: number;
    /** Mail waiting unread in mailboxes, before collection. A gauge — never sum across time. */
    backlog?: number;
    lastConnectionError?: string;
}

/**
 * Returned by both Core routes so one row component can render both views.
 *
 * `collectionHealth` and `lastSuccessAt` are landing-view only and are always
 * absent on a history row (contract §2.2), which is why they are optional here.
 */
export interface TenantMailProcessingRow {
    tenantKey?: string;
    name?: string;
    collectionHealth?: TenantCollectionHealth;
    lastOutcome?: TenantCollectionOutcome;
    lastAttemptAt?: string;
    lastSuccessAt?: string;
    error?: string;
    inFlight?: TenantInFlightCounts;
    terminal?: TenantTerminalCounts;
    throughput?: TenantThroughputDeltas;
    mailboxes?: TenantMailboxRollup;
}

export interface TenantOverview {
    /** Server-ordered worst-first. Do not re-sort on load. */
    tenants?: TenantMailProcessingRow[];
    asOf: string;
}

export interface TenantHistory {
    tenantKey?: string;
    name?: string;
    from: string;
    to: string;
    /** Oldest to newest. Empty array is a valid answer, not a failure. */
    rows?: TenantMailProcessingRow[];
    asOf: string;
}

/**
 * Reads one row per configured tenant, across ALL tenants.
 *
 * This route ignores the tenant claim in the token (contract §3.1) — any
 * sys-admin token works and the token does not select which tenants are
 * returned. That is unlike every tenant-scoped route in this API, so do not
 * infer scoping from the fact that a token is passed.
 *
 * Rows come back server-ordered worst-first, unpaged and unfiltered.
 */
export async function GetTenantOverview(sysAdminToken: string): Promise<iEmsApiResponseBody<TenantOverview>> {
    try {
        return await CallEmsApi<TenantOverview>(sysAdminToken, "GET", "Core/MailProcessing/Tenants");
    } catch {
        return networkFailure<TenantOverview>();
    }
}

/**
 * Reads stored snapshots for ONE tenant over a time window.
 *
 * The tenant is selected by the `tenantKey` argument, NOT by the token —
 * this route ignores the tenant claim like its sibling above.
 *
 * `from` and `to` are both required by the server, the window is inclusive,
 * and a span wider than 2 days is rejected outright rather than truncated.
 * Constrain the range before calling; a 400 here is a client bug.
 *
 * PHASE-2 SURFACE: no non-test caller yet. This is the request the history
 * fan-out (backlogTrend.ts) will use to populate `behindRuns` per tenant;
 * until then it is exercised only by CoreAPI.test.ts. Not dead code — the
 * deliberate seam for the deferred per-tenant history feature.
 */
export async function GetTenantHistory(
    sysAdminToken: string,
    tenantKey: string,
    from: Date,
    to: Date,
): Promise<iEmsApiResponseBody<TenantHistory>> {
    // Built OUTSIDE the try deliberately. toEmsApiTimestamp throws on an
    // Invalid Date, and that is a caller bug, not a network failure — letting
    // it propagate surfaces it at the call site. Catching it here would
    // report a broken caller as an unreachable tenant, which is precisely the
    // confusion this feature exists to prevent.
    const path =
        `Core/MailProcessing/Tenants/${encodeURIComponent(tenantKey)}/History` +
        `?from=${encodeURIComponent(toEmsApiTimestamp(from))}` +
        `&to=${encodeURIComponent(toEmsApiTimestamp(to))}`;

    try {
        return await CallEmsApi<TenantHistory>(sysAdminToken, "GET", path);
    } catch {
        return networkFailure<TenantHistory>();
    }
}
